import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Container,
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';

const LAST_UPDATED = '13 March 2026';

function PrivacyPolicyPage() {
  const sections = useMemo(() => ([
    {
      id: 'overview',
      title: '1. Overview',
      paragraphs: [
        'This Privacy Policy describes how Virtual Medic collects, uses, stores, and shares personal information when you use our web portal, Unity application, and related services.',
        'By creating an account or using Virtual Medic, you acknowledge this policy and consent to the processing described below where consent is the legal basis.',
      ],
    },
    {
      id: 'information-we-collect',
      title: '2. Information We Collect',
      paragraphs: [
        'We collect account and profile details such as name, email address, role, institution, authentication details, and billing metadata.',
        'We also collect service usage information, including login activity, session performance analytics, assessment outputs, and support interactions.',
      ],
      bullets: [
        'Identity and contact data: first name, last name, email address, institution details.',
        'Account and security data: password hash, authentication provider, login events, and audit logs.',
        'Learning and product data: case progress, submissions, feedback, and usage telemetry.',
        'Payment data: billing events and transaction references from approved payment processors.',
      ],
    },
    {
      id: 'how-we-use-information',
      title: '3. How We Use Information',
      paragraphs: [
        'We use personal information to provide and secure the service, manage user accounts, deliver educational insights, process payments, and respond to support requests.',
        'We may also use aggregated or de-identified data to improve product quality, reporting, and service reliability.',
      ],
    },
    {
      id: 'legal-bases',
      title: '4. Legal Bases for Processing',
      paragraphs: [
        'Depending on your location and relationship with Virtual Medic, we process information under one or more legal bases: performance of a contract, legitimate interests, legal obligations, and consent.',
        'When consent is required, you can withdraw it at any time without affecting processing that occurred before withdrawal.',
      ],
    },
    {
      id: 'sharing-and-disclosure',
      title: '5. Sharing and Disclosure',
      paragraphs: [
        'We share data only when necessary to operate the service, comply with legal obligations, or protect rights and safety.',
        'Service providers receive access only to the minimum information needed to perform their contracted work.',
      ],
      bullets: [
        'Infrastructure and hosting providers for secure platform operation.',
        'Payment processors for subscription and token purchases.',
        'Analytics and monitoring tools for reliability and performance.',
        'Law enforcement or regulators when required by law.',
      ],
    },
    {
      id: 'data-retention',
      title: '6. Data Retention',
      paragraphs: [
        'We retain personal information only for as long as necessary for the purposes outlined in this policy, including legal, accounting, and security obligations.',
        'Retention periods vary by data type, and we securely delete or anonymize data when it is no longer needed.',
      ],
    },
    {
      id: 'security',
      title: '7. Security Measures',
      paragraphs: [
        'Virtual Medic applies administrative, technical, and organizational controls to protect data against unauthorized access, disclosure, alteration, and destruction.',
        'No system is completely risk-free, but we continuously monitor and improve our security posture.',
      ],
    },
    {
      id: 'international-transfers',
      title: '8. International Data Transfers',
      paragraphs: [
        'If personal information is transferred outside your country, we use appropriate safeguards required by applicable law, such as contractual clauses or equivalent mechanisms.',
      ],
    },
    {
      id: 'your-rights',
      title: '9. Your Rights',
      paragraphs: [
        'Subject to applicable law, you may request access, correction, deletion, restriction, or portability of your personal information.',
        'You may also object to certain processing and lodge a complaint with a data protection authority.',
      ],
      footer: 'To exercise your rights, contact privacy@virtualmedic.uk from your registered account email.',
    },
    {
      id: 'children',
      title: '10. Children and Minors',
      paragraphs: [
        'Virtual Medic is intended for adult learners and authorized institutional users. We do not knowingly collect personal information from children without appropriate authorization.',
      ],
    },
    {
      id: 'changes',
      title: '11. Changes to This Policy',
      paragraphs: [
        'We may update this Privacy Policy from time to time. Material updates will be communicated through the platform or by email where required.',
      ],
    },
    {
      id: 'contact',
      title: '12. Contact',
      paragraphs: [
        'If you have questions about this policy or privacy practices, contact support@virtualmedic.uk.',
      ],
    },
  ]), []);

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box component="main" sx={{ py: 6 }} aria-labelledby="privacy-policy-title">
      <Container maxWidth="md">
        <Typography id="privacy-policy-title" variant="h3" component="h1" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Last updated: {LAST_UPDATED}
        </Typography>

        <Box
          component="nav"
          aria-label="Privacy policy sections"
          sx={{ mb: 4, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        >
          <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
            On this page
          </Typography>
          <List dense sx={{ listStyleType: 'disc', pl: 3 }}>
            {sections.map((section) => (
              <ListItem key={section.id} sx={{ display: 'list-item', py: 0.25 }}>
                <ListItemText
                  primary={(
                    <MuiLink href={`#${section.id}`} underline="hover">
                      {section.title}
                    </MuiLink>
                  )}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 2,
            '@media print': { display: 'none' },
          }}
        >
          <Button variant="outlined" color="primary" onClick={handlePrint}>
            Download / Print PDF
          </Button>
        </Box>

        {sections.map((section) => (
          <Box key={section.id} id={section.id} component="section" sx={{ mb: 4 }} aria-labelledby={`${section.id}-title`}>
            <Typography id={`${section.id}-title`} variant="h5" component="h2" gutterBottom>
              {section.title}
            </Typography>
            {section.paragraphs.map((paragraph) => (
              <Typography key={paragraph} variant="body1" sx={{ mb: 2 }}>
                {paragraph}
              </Typography>
            ))}
            {section.bullets && (
              <List sx={{ listStyleType: 'disc', pl: 4 }}>
                {section.bullets.map((bullet) => (
                  <ListItem key={bullet} sx={{ display: 'list-item', pl: 0 }}>
                    <ListItemText primaryTypographyProps={{ variant: 'body1' }} primary={bullet} />
                  </ListItem>
                ))}
              </List>
            )}
            {section.footer && (
              <Typography variant="body2" color="text.secondary">
                {section.footer}
              </Typography>
            )}
          </Box>
        ))}
      </Container>
    </Box>
  );
}

export default PrivacyPolicyPage;
